import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import logoTiffany from '../assets/logo_tiffany.png';
import logoGeorge from '../assets/logo_george.png';

const UserIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { authState, oktaAuth } = useOktaAuth();
  const [userName, setUserName] = useState('');
  const isAuthenticated = Boolean(authState?.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      setUserName('');
      return;
    }
    void oktaAuth.getUser().then((user) => {
      setUserName(user.name ?? user.email ?? '');
    });
  }, [isAuthenticated, oktaAuth]);

  const login = () => {
    void oktaAuth.signInWithRedirect({ originalUri: window.location.href });
  };
  const logout = () => {
    void oktaAuth.signOut({ postLogoutRedirectUri: window.location.origin + '/' });
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  const prevLocation = React.useRef(location);
  useEffect(() => {
    if (prevLocation.current !== location) {
      setMenuOpen(false);
      prevLocation.current = location;
    }
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [menuOpen]);

  const isActive = (path: string) => location.pathname === path;

  const getLinkClasses = (path: string, isMobile = false) => {
    const base = isMobile
      ? 'text-2xl font-medium py-4 border-b border-gray-100 w-full text-center'
      : 'px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-full transition-colors whitespace-nowrap';

    const active = isActive(path)
      ? isMobile
        ? 'text-black'
        : 'text-black bg-gray-100'
      : 'text-gray-500 hover:text-black hover:bg-gray-50';

    return `${base} ${active}`;
  };

  return (
    <>
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled || menuOpen
            ? 'bg-white/90 backdrop-blur-md border-b border-gray-100 py-3 shadow-sm'
            : 'bg-transparent py-4 md:py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative flex items-center justify-between h-14">
          {/* Left: Tiffany Logo */}
          <div className="flex-1 flex justify-start items-center z-50">
            <img
              src={logoTiffany}
              alt="Tiffany & Co."
              className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-3 md:h-4' : 'h-4 md:h-5'}`}
            />
          </div>

          {/* Center: George Logo (Visible on both Mobile and Desktop) */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 justify-center z-[60]">
            <Link
              to="/"
              className="group relative block"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                }
                setMenuOpen(false);
                document.body.style.overflow = 'unset';
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 50);
              }}
            >
              <img
                src={logoGeorge}
                alt="George Logo"
                className={`w-auto object-contain transition-all duration-300 ${scrolled || menuOpen ? 'h-9 md:h-10' : 'h-10 md:h-12'}`}
              />
              <span className="hidden md:hidden text-2xl font-bold tracking-tighter text-black uppercase">
                GEORGE
              </span>
            </Link>
          </div>

          {/* Right: Navigation (Desktop) & Hamburger (Mobile) */}
          <div className="flex-1 flex justify-end items-center gap-1 md:gap-2 z-50">
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-2">
              <Link to="/" className={getLinkClasses('/')}>
                Agent Store
              </Link>
              <Link to="/contact" className={getLinkClasses('/contact')}>
                Contact
              </Link>
              {isAuthenticated ? (
                <div className="flex items-center gap-2 ml-1">
                  <span className="text-xs text-gray-500 font-medium max-w-[120px] truncate">
                    {userName}
                  </span>
                  <button
                    onClick={logout}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-full text-gray-500 hover:text-black hover:bg-gray-50 transition-colors"
                  >
                    <UserIcon />
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={login}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-full text-gray-500 hover:text-black hover:bg-gray-50 transition-colors ml-1"
                >
                  <UserIcon />
                  Login
                </button>
              )}
            </div>

            {/* Mobile Hamburger Button */}
            <button
              className="md:hidden p-2 text-black focus:outline-none"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                <span
                  className={`w-full h-0.5 bg-black rounded-full transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}
                />
                <span
                  className={`w-full h-0.5 bg-black rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0' : 'opacity-100'}`}
                />
                <span
                  className={`w-full h-0.5 bg-black rounded-full transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2.5' : ''}`}
                />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-white z-40 pt-24 px-6 flex flex-col items-center transition-all duration-300 md:hidden ${
          menuOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-10 pointer-events-none'
        }`}
      >
        <div className="flex flex-col w-full max-w-sm gap-4">
          <Link to="/" className={getLinkClasses('/', true)}>
            Agent Store
          </Link>
          <Link to="/contact" className={getLinkClasses('/contact', true)}>
            Contact
          </Link>
          {isAuthenticated ? (
            <>
              <span className="text-sm text-gray-400 text-center">{userName}</span>
              <button
                onClick={logout}
                className="text-2xl font-medium py-4 border-b border-gray-100 w-full text-center text-gray-500 hover:text-black"
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={login}
              className="text-2xl font-medium py-4 border-b border-gray-100 w-full text-center text-gray-500 hover:text-black"
            >
              Login
            </button>
          )}
        </div>

        <div className="mt-auto mb-12 text-center text-gray-400 text-xs">
          <p>Tiffany & Co. George Agent Store</p>
        </div>
      </div>
    </>
  );
};

export default Header;
